import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { helpers, enums, format } from "../../../../common/lib";

/**
 * useAssetHistoryData
 * - Builds full history (notes/images/documents/seen/excluded/approved/rejected)
 * - Places assetDecision (approved/rejected) on top,
 *   then latestStatus (seen/excluded),
 *   Sorts by date (newest first) rest of the timeline
 *
 * Returns: { history, decisionEntry, latestStatus }
 */
const useAssetHistoryData = (data) => {
    const { typesList, accounts, auditAssets } = useSelector((state) => state);

    const currentAsset = auditAssets?.data?.find(
        (item) => item.id === data?.[0]?.auditAsset
    );

    const exclusionDatePickers = [
        enums.status.exclusionReason.assetIsSold,
        enums.status.exclusionReason.assetDeliveredToCustomerAndNotPaid
    ];

    /**
     * Guards on a real field rather than on truthiness: helpers.getListItemForId
     * returns {} when an id is missing from the store, which is truthy and would
     * otherwise render as "undefined undefined".
     */
    const getUser = (account, actorId) => ({
        name: account?.firstName
            ? `${account.firstName} ${account.surname || ""}`.trim()
            : "",
        nameKey:
            !account?.firstName && actorId != null ? "userNotFound" : null,
        avatarPath: account?.avatarPath || ""
    });

    const buildEntry = ({ user, date, heading, extra = {} }) => ({
        user,
        date: date || "",
        heading,
        ...extra
    });

    const getInfoDetail = (item, subItem, type) => {
        const auditorUser = item.auditor;
        const user = getUser(auditorUser, item.auditorId);

        const isController = Object.keys(item).includes(
            "assetControllerAuxiliary"
        );
        const controllerUser = getUser(
            accounts.data?.[accounts.byId?.[item.controllerId]],
            item.controllerId
        );

        const createdDate = subItem?.createdDate || item.assignedDate;
        const formattedDate = format.dateTimeNew(createdDate);

        if (type === "rejected") {
            const reason = helpers.getListItemForId(
                typesList.rejectionReasons,
                item.rejectionReason
            );
            return buildEntry({
                user: isController ? controllerUser : user,
                date: format.dateTimeNew(item.date),
                heading: "assetRejected",
                extra: {
                    rejectionDate: format.dateTimeNew(item.date),
                    rejectionReason: reason?.name
                }
            });
        }

        if (type === "approved") {
            return buildEntry({
                user: isController ? controllerUser : user,
                date: formattedDate,
                heading: "assetApproved"
            });
        }

        if (type === "image") {
            const imageType = helpers.getListItemForId(
                typesList.imageTypes,
                subItem?.imageType
            );
            return buildEntry({
                user: isController ? controllerUser : user,
                date: formattedDate,
                heading: "addedNewImage",
                extra: {
                    imageDetail: imageType?.name
                }
            });
        }

        /**  Handle documents */
        if (type === "document") {
            /* For future use 
            When we get more document types
             const documentType = helpers.getListItemForId(
                 typesList.documentTypes,
                 subItem?.documentType
             );*/
            return buildEntry({
                user: isController ? controllerUser : user,
                date: formattedDate,
                heading: "addedNewEvidence",
                extra: {
                    documentDetail: "pdf",
                    documentPath: subItem?.path
                }
            });
        }

        if (type === "notes" && item.exclusionReason === null) {
            return buildEntry({
                user: isController ? controllerUser : user,
                date: formattedDate,
                heading: "assetComment",
                extra: {
                    note: subItem?.content || ""
                }
            });
        }

        if (type !== "rejected" && item.exclusionReason) {
            const isPicker = exclusionDatePickers.includes(
                item.exclusionReason.id
            );
            return buildEntry({
                user: isController ? controllerUser : user,
                date: formattedDate,
                heading: "assetExcluded",
                extra: {
                    systemActorKey:
                        item.auditorId == null && item.controllerId == null
                            ? "excludedByCheckVentory"
                            : null,
                    note: subItem?.content || "",
                    exclusionReason: item.exclusionReason,
                    exclusionReasonDate: isPicker
                        ? format.dateTimeNew(item.exclusionReasonDate)
                        : null
                }
            });
        }

        if (!item.controller) {
            return buildEntry({
                user: isController ? controllerUser : user,
                date: formattedDate,
                heading: "assetSeen"
            });
        }

        return null;
    };

    const { history, decisionEntry, latestStatus } = useMemo(() => {
        const assetHistory = [];

        if (!currentAsset) {
            return { history: [], decisionEntry: null, latestStatus: null };
        }

        const updatedByUser =
            accounts.data?.[accounts.byId?.[currentAsset.updatedBy]];

        // Approval / rejection entries
        if (
            currentAsset.isApproved === false &&
            Array.isArray(currentAsset.assetRejection)
        ) {
            currentAsset.assetRejection.forEach((rej) => {
                const entry = getInfoDetail(
                    { ...rej, auditor: updatedByUser, date: rej.rejectionDate },
                    null,
                    "rejected"
                );
                if (entry) assetHistory.push(entry);
            });
        }

        if (currentAsset.isApproved) {
            const entry = getInfoDetail(
                { ...currentAsset, auditor: updatedByUser, date: "" },
                null,
                "approved"
            );
            if (entry) assetHistory.push(entry);
        }

        /** Build history from data array */
        data?.forEach((item) => {
            if (!item) return;

            // Notes
            if (item.auditNotes?.length) {
                item.auditNotes.forEach((note) => {
                    const entry = getInfoDetail(item, note, "notes");
                    if (entry) assetHistory.push(entry);
                });
            } else {
                const entry = getInfoDetail(item);
                if (entry) assetHistory.push(entry);
            }

            // Images
            item.auditImages?.forEach((img) => {
                const entry = getInfoDetail(item, img, "image");
                if (entry) assetHistory.push(entry);
            });

            /** 🔽 Add documents after images */
            item.auditDocuments?.forEach((doc) => {
                const entry = getInfoDetail(item, doc, "document");
                if (entry) assetHistory.push(entry);
            });
        });

        // Sort by date descending
        const sorted = helpers.sortByDate(assetHistory, "date", "desc");

        // Find latest decision entry
        const decisionEntry = sorted.find(
            (e) =>
                e.heading === "assetApproved" || e.heading === "assetRejected"
        );

        // Find latest status entry before decision
        let latestStatus = null;
        if (decisionEntry) {
            const decisionIndex = sorted.indexOf(decisionEntry);
            for (let i = decisionIndex + 1; i < sorted.length; i++) {
                const e = sorted[i];
                if (
                    e.heading === "assetSeen" ||
                    e.heading === "assetExcluded"
                ) {
                    latestStatus = e;
                    break;
                }
            }
        } else {
            latestStatus = sorted.find(
                (e) =>
                    e.heading === "assetSeen" || e.heading === "assetExcluded"
            );
        }

        // Build combined: decision → latestStatus → rest
        const rest = sorted.filter(
            (e) => e !== decisionEntry && e !== latestStatus
        );
        const combined = [
            ...(decisionEntry ? [decisionEntry] : []),
            ...(latestStatus ? [latestStatus] : []),
            ...rest
        ];

        return { history: combined, decisionEntry, latestStatus };
    }, [data, currentAsset, accounts, typesList]);

    return { history, decisionEntry, latestStatus };
};

export default useAssetHistoryData;
